<template>
  <div class="row justify-content-md-center px-4">
    <div class="col-12 form-group p-4">
      <div class="row justify-content-md-start">
        <div class="col-md-6">
          <label class="form-control-label">Grupo</label>
          <multiselect
            v-model="internalValue.grupos"
            :options="grupos"
            track-by="id"
            label="descricao"
            placeholder="Selecione um grupo"
            :multiple="true"
            :close-on-select="false"
            :limit="1"
            :limit-text="limitText"
            open-direction="bottom"
            select-all
          />
        </div>
        <div class="col-md-6">
          <label class="form-control-label">Subgrupo</label>
          <multiselect
            v-model="internalValue.subgrupos"
            :options="filteredSubgroups"
            track-by="id"
            label="descricao"
            placeholder="Selecione um subgrupo"
            :multiple="true"
            :close-on-select="false"
            :limit="1"
            open-direction="bottom"
            :limit-text="limitText"
            select-all
          />
        </div>
        <div class="col-md-12">
          <label class="form-control-label">Marca</label>
          <multiselect
            v-model="internalValue.brands"
            :options="brands"
            track-by="id"
            label="name"
            placeholder="Selecione uma marca"
            :multiple="true"
            :close-on-select="false"
            :limit="1"
            :limit-text="limitText"
            open-direction="bottom"
            infinite-scroll
            select-all
            @search-change="searchBrand"
            @bottom-reached="searchBrand(null, brandsPage + 1)"
            @open="emit('grow-container')"
            @close="emit('shrink-container')"
          />
        </div>
        <div class="col-md-12"> 
          <label class="form-control-label">Produtos</label>
          <multiselect
            v-model="internalValue.preferredproducts"
            :options="preferredProducts"
            track-by="id"
            label="name"
            :multiple="true"
            :close-on-select="false"
            placeholder="Selecione um Produto"
            :limit="1"
            :limit-text="limitText"
            open-direction="bottom"
            :loading="isLoadingProducts"
            @search-change="debouncedSearchPreferredProducts"
            select-all
            @open="emit('grow-container')"
            @close="emit('shrink-container')"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch, onMounted, computed } from "vue";
import Multiselect from "@/components/Multiselect/translated_multiselect.vue";
import axios from "@/backend/axios/index.js";
import useModel from "@/composables/useModel";

const { grupos, fetchGrupos } = useModel('grupo', '/api/v1/grupos');
const { subgrupos, fetchSubgrupos } = useModel('subgrupo', '/api/v1/subgrupos');

const props = defineProps({
  value:    { type: Object, required: true },
  initialData: { type: Object }
});

const internalValue                  = ref({});
const brands                         = ref([]);
const preferredProducts              = ref([]);
const searchPreferredProductsTimeout = ref(null);
const getBrandsTimeout               = ref(null);
const brandSearchQuery               = ref(null);
const isLoadingProducts              = ref(false);
const brandsPage                     = ref(1);
const updatingInitialData            = ref(false)

const emit = defineEmits(["input", "grow-container", "shrink-container"]);

watch(internalValue, (newValue) => {
  if (updatingInitialData.value) return;

  emit("input", newValue) 
}, { deep: true });

const populateInitialData = () => {
  updatingInitialData.value = true
  internalValue.value = { ...props.initialData }
  updatingInitialData.value = false
}

const searchPreferredProducts = async (name = null) => {
  isLoadingProducts.value = true;

  try {
    const queryParams = { status: 'active' };

    if (internalValue.value.grupos && internalValue.value.grupos.length) queryParams.grupo_id = internalValue.value.grupos.map(e => e.id);
    if (internalValue.value.subgrupos && internalValue.value.subgrupos.length) queryParams.subgrupo_id = internalValue.value.subgrupos.map(e => e.id);
    if (internalValue.value.brands && internalValue.value.brands.length) queryParams.brand_id = internalValue.value.brands.map(e => e.id);
    if (name) queryParams.name = name;

    const response = await axios.get("/api/v1/preferredproducts", { params: { name_only: true, q: queryParams } });

    preferredProducts.value = response.data;
  } catch (error) {
    console.error(error);
  } finally {
    isLoadingProducts.value = false;
  }
};

const filteredSubgroups = computed(() => {
  if (!internalValue.value.grupos)             return subgrupos.value;
  if (internalValue.value.grupos.length === 0) return subgrupos.value;

  return subgrupos.value.filter(subgroup => {
    return subgroup.grupo_ids && subgroup.grupo_ids.some(grupoId => {
      return internalValue.value.grupos.some(group => group.id === grupoId)
    });
  });
});

const debouncedSearchPreferredProducts = () => {
  if (searchPreferredProductsTimeout.value) clearTimeout(searchPreferredProductsTimeout.value);

  searchPreferredProductsTimeout.value = setTimeout(() => searchPreferredProducts(), 500);
};

const searchBrand = (query, page = 1) => {
  brandSearchQuery.value = query;
  brandsPage.value = page;
  debouncedGetBrands();
};

const getBrands = async () => {
  const params = { page: brandsPage.value, q: {} };
  if (brandSearchQuery.value) params.q.name = brandSearchQuery.value;
  if (internalValue.value.grupos && internalValue.value.grupos.length) params.q.group_id = internalValue.value.grupos.map(e => e.id);
  if (internalValue.value.subgrupos && internalValue.value.subgrupos.length) params.q.subgroup_id = internalValue.value.subgrupos.map(e => e.id);

  try {
    const response = await axios.get("/api/v2/brands", { params });
    brands.value = brandsPage.value === 1 ? response.data.data : brands.value.concat(response.data.data);
  } catch (error) {
    console.error(error);
  }
};

const debouncedGetBrands = () => {
  if (getBrandsTimeout.value) clearTimeout(getBrandsTimeout.value);

  getBrandsTimeout.value = setTimeout(() => getBrands(), 500);
};

watch(() => internalValue.value.grupos, () => {
  debouncedGetBrands()
  debouncedSearchPreferredProducts()
}, { deep: true });
watch(() => internalValue.value.subGrupos, () => {
  debouncedGetBrands()
  debouncedSearchPreferredProducts() 
}, { deep: true });
watch(() => internalValue.value.brands, () => {
  debouncedSearchPreferredProducts() 
}, { deep: true });

onMounted(() => {
  fetchGrupos();
  fetchSubgrupos();
  debouncedGetBrands();
  searchPreferredProducts();
  populateInitialData()
});

const limitText = (count) => `+ ${count}`;
</script>

<style lang="scss" scoped>
.form-group {
  background-color: #e9ecef;
  border-radius: 10px;
}
</style>
